import React from 'react';
import { Lock } from 'lucide-react';
import { Badge, fmtInr, fmtDate } from '../ui';

const TONE = {
  draft: 'gray', sent: 'blue', accepted: 'green', lost: 'red', converted: 'navy',
  'Pending Approval': 'amber', 'Partially Approved': 'amber', Approved: 'green', Rejected: 'red', 'On Hold': 'gray',
  Invoiced: 'navy', 'Partially Invoiced': 'blue', Cancelled: 'gray',
  pending: 'amber', approved: 'green', rejected: 'red', hold: 'gray',
  unpaid: 'red', partial: 'amber', paid: 'green', dispatched: 'blue', delivered: 'green',
  open: 'amber', partially_ordered: 'blue', ordered: 'navy', closed: 'gray', partially_received: 'blue', received: 'green', cancelled: 'gray',
};
export const StatusBadge = ({ status, testid }) => <Badge tone={TONE[status] || 'gray'} data-testid={testid} className="capitalize">{String(status || '').replace(/_/g, ' ')}</Badge>;

export const PartyCell = ({ name, code, hidden }) => (
  <div className="flex items-center gap-2 min-w-0">
    {hidden && <Lock size={12} className="text-amber-500 flex-shrink-0" />}
    <div className="min-w-0"><p className="font-semibold truncate">{name}</p>{!hidden && code && <p className="text-[11px] font-mono" style={{ color: 'var(--erp-muted)' }}>{code}</p>}</div>
  </div>
);

export const DocNumber = ({ number, date }) => (
  <div><p className="font-mono text-xs font-bold">{number}</p><p className="text-[11px]" style={{ color: 'var(--erp-muted)' }}>{fmtDate(date)}</p></div>
);

export const Meta = ({ items }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
    {items.filter(Boolean).map(([l, v]) => (
      <div key={l} className="rounded-xl p-3" style={{ background: '#f5f7fb' }}>
        <p className="text-[10.5px] font-semibold uppercase tracking-wider" style={{ color: 'var(--erp-muted)' }}>{l}</p>
        <div className="text-sm font-bold mt-0.5 break-words">{v ?? '—'}</div>
      </div>
    ))}
  </div>
);

export const Totals = ({ doc }) => (
  <div className="ml-auto w-full sm:w-72 text-sm space-y-1 mt-4" data-testid="doc-totals">
    <div className="flex justify-between"><span style={{ color: 'var(--erp-muted)' }}>Taxable</span><span className="font-semibold">{fmtInr(doc.subtotal)}</span></div>
    {doc.intra_state ? <>
      <div className="flex justify-between"><span style={{ color: 'var(--erp-muted)' }}>CGST</span><span>{fmtInr(doc.cgst)}</span></div>
      <div className="flex justify-between"><span style={{ color: 'var(--erp-muted)' }}>SGST</span><span>{fmtInr(doc.sgst)}</span></div>
    </> : <div className="flex justify-between"><span style={{ color: 'var(--erp-muted)' }}>IGST</span><span>{fmtInr(doc.igst)}</span></div>}
    <div className="flex justify-between text-base pt-1.5 border-t" style={{ borderColor: 'var(--erp-line)' }}><span className="font-bold">Total</span><span className="font-extrabold" style={{ color: 'var(--erp-navy)' }}>{fmtInr(doc.total)}</span></div>
  </div>
);

// Read-only line table for drawers. extra: [{label, render}] appended columns.
export const DocLines = ({ items = [], extra = [], showPrices = true }) => (
  <div className="rounded-xl border overflow-x-auto erp-scroll" style={{ borderColor: 'var(--erp-line)' }}>
    <table className="w-full text-sm">
      <thead><tr className="text-[11px] uppercase tracking-wider" style={{ background: '#f8fafd', color: 'var(--erp-muted)' }}>
        <th className="text-left font-semibold px-3 py-2">Item</th><th className="text-right font-semibold px-2 py-2">Qty</th>
        {showPrices && <><th className="text-right font-semibold px-2 py-2">Rate</th><th className="text-right font-semibold px-2 py-2">Disc</th><th className="text-right font-semibold px-3 py-2">Amount</th></>}
        {extra.map((e) => <th key={e.label} className="text-right font-semibold px-2 py-2">{e.label}</th>)}
      </tr></thead>
      <tbody>
        {items.map((l) => (
          <tr key={l.id} className="border-t" style={{ borderColor: 'var(--erp-line)' }} data-testid={`doc-line-${l.id}`}>
            <td className="px-3 py-2"><p className="font-semibold">{l.sku_name}</p><p className="text-[11px] font-mono" style={{ color: 'var(--erp-muted)' }}>{l.sku_code}{l.gst_rate != null ? ` · GST ${l.gst_rate}%` : ''}</p></td>
            <td className="px-2 py-2 text-right font-semibold">{l.qty} <span className="text-[11px] font-normal" style={{ color: 'var(--erp-muted)' }}>{l.uom}</span></td>
            {showPrices && <><td className="px-2 py-2 text-right">{fmtInr(l.rate)}</td><td className="px-2 py-2 text-right">{l.discount_pct ? `${l.discount_pct}%` : '—'}</td><td className="px-3 py-2 text-right font-bold">{fmtInr(l.amount)}</td></>}
            {extra.map((e) => <td key={e.label} className="px-2 py-2 text-right">{e.render(l)}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
